import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field, FieldArray, ErrorMessage } from 'formik';
import type { FormikValues } from 'formik';
import {RootState, useAppSelector, useAppDispatch, loadChange, resetChanges, updateChange} from '../../../redux';
import { loadProjects } from '../../../redux';
import { Goal, Change, Project, Sprint } from '../../../models';
import {
    MultiSelectField, ChangeListItem,
    ChangePicker, Button, ChangeModal,
    ToggleField, DatepickerField, LabelWithHelp,
    FormActions, DangerZone, Icon
} from '../../../components';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';

interface GoalFormProps {
    isCreation: boolean,
    initialValues: FormikValues,
    onSubmit: (values: AnyObj) => void
}

function isoStamp(value: any) {
    if (value == null || value === '')
        return null;
    if (typeof value.toISOString === 'function')
        return value.toISOString();
    const parsed = moment(value);
    return parsed.isValid() ? parsed.toISOString() : null;
}

/**
 * Fields the user edits on this form. Card updates must not change this,
 * or enableReinitialize wipes the next unsaved edit.
 */
function editableSignature(values: AnyObj) {
    const milestones = Goal.sortMilestones(values?.milestones || []);
    const phases = Goal.sortPhases(values?.phases || [], milestones);
    return JSON.stringify({
        name: values?.name || '',
        startAt: isoStamp(values?.startAt),
        stopAt: isoStamp(values?.stopAt),
        projects: (values?.projects || []).map((project: AnyObj) => project?._id || project),
        autoAttachProjectChanges: Boolean(values?.autoAttachProjectChanges),
        milestones: milestones.map((milestone: AnyObj) => ({
            _id: milestone._id,
            name: milestone.name,
            at: isoStamp(milestone.at)
        })),
        phases: phases.map((phase: AnyObj) => ({
            _id: phase._id,
            name: phase.name,
            startAt: isoStamp(phase.startAt),
            stopAt: isoStamp(phase.stopAt),
            startMilestone: phase.startMilestone || null,
            stopMilestone: phase.stopMilestone || null
        }))
    });
}

/** Bounds errors are not form fields, so Formik never marks them touched and ErrorMessage stays hidden. */
function SubmitErrorSummary({ submitCount, errors }: { submitCount: number; errors: AnyObj }) {
    const ref = React.useRef<HTMLDivElement>(null);
    const messages = Object.entries(errors || {}).filter((entry) => typeof entry[1] === 'string') as [string, string][];

    useEffect(() => {
        if (submitCount > 0 && messages.length && ref.current)
            ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [submitCount, messages.length]);

    if (!submitCount || !messages.length)
        return null;

    return (
        <div ref={ref} className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200 dark:border-red-800">
            <ul className="list-disc space-y-1 pl-5">
                {messages.map(([key, message]) => (
                    <li key={key}>{message}</li>
                ))}
            </ul>
        </div>
    );
}

/** Refresh the card list without resetting the rest of the form. */
function SyncChanges({ changes, setFieldValue }: {
    changes: Change[];
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}) {
    const current = React.useRef(changes);
    useEffect(() => {
        if (current.current === changes)
            return;
        current.current = changes;
        setFieldValue('changes', changes || [], false);
    }, [changes, setFieldValue]);
    return null;
}

/** Keep milestones/phases ordered by date while editing. */
function KeepSortedByDate({ values, setFieldValue }: {
    values: FormikValues;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}) {
    useEffect(() => {
        const milestones = values.milestones || [];
        const phases = values.phases || [];
        const sortedMilestones = Goal.sortMilestones(milestones);
        const sortedPhases = Goal.sortPhases(phases, sortedMilestones);

        const sameOrder = (a: AnyObj[], b: AnyObj[]) =>
            a.length === b.length && a.every((item, i) => item?._id === b[i]?._id);

        if (!sameOrder(milestones, sortedMilestones))
            setFieldValue('milestones', sortedMilestones, false);
        if (!sameOrder(phases, sortedPhases))
            setFieldValue('phases', sortedPhases, false);
    }, [values.milestones, values.phases, setFieldValue]);

    return null;
}

function GoalForm(props: GoalFormProps) {
    const { isCreation, initialValues, onSubmit } = props;

    const { t }    = useTranslation();
    const dispatch = useAppDispatch();

    const [currentChangeId, setCurrentChangeId]           = useState<string | undefined>();
    const [changesPickerVisible, setChangesPickerVisible] = useState(!(initialValues?.changes?.length > 0));
    const [assignPhaseId, setAssignPhaseId]               = useState<string>('');

    const { projects } = useAppSelector((state : RootState) => state.projects);
    const { updating } = useAppSelector((state: RootState) => state.goals);
    const { change }   = useAppSelector(state => state.changes);

    useEffect(() => {
        dispatch(loadProjects());
    },[dispatch]);

    const goal = new Goal({
        ...initialValues,
        changes: initialValues?.changes || [],
        milestones: initialValues?.milestones || [],
        phases: initialValues?.phases || [],
    });

    const signature = editableSignature(initialValues || {});
    const formInitialValues = React.useMemo(() => ({
        ...initialValues,
        projects: (initialValues?.projects || []).map((project: Project) => project?._id || project),
        changes: initialValues?.changes || [],
        milestones: Goal.sortMilestones((initialValues?.milestones || []).map((m: AnyObj) => ({
            _id: m._id,
            name: m.name,
            at: m.at
        }))),
        phases: Goal.sortPhases((initialValues?.phases || []).map((p: AnyObj) => ({
            _id: p._id,
            name: p.name,
            startAt: p.startAt,
            stopAt: p.stopAt,
            startMilestone: p.startMilestone || '',
            stopMilestone: p.stopMilestone || ''
        })), initialValues?.milestones || []),
        autoAttachProjectChanges: Boolean(initialValues?.autoAttachProjectChanges)
    // The signature is the stable snapshot of editable fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [signature]);

    const selectChange = ((change?: Change) => {
        setCurrentChangeId(change?._id);
        if (change)
            dispatch(loadChange(change._id));
        else
            dispatch(resetChanges(/*onlyItem*/true));
    });

    const validate = (values: AnyObj) => {
        const invalid: AnyObj = {};

        if (!values.name)
            invalid.name = t('common.validation.this_field_is_required');

        (values.milestones || []).forEach((m: AnyObj, index: number) => {
            if (!m.name)
                invalid[`milestones.${index}.name`] = t('common.validation.this_field_is_required');
            if (!m.at)
                invalid[`milestones.${index}.at`] = t('common.validation.this_field_is_required');
        });

        const milestoneById = new Map(
            (values.milestones || [])
                .filter((m: AnyObj) => m._id)
                .map((m: AnyObj) => [String(m._id), m])
        );
        const resolvedPhases = (values.phases || []).map((p: AnyObj, index: number) => {
            if (!p.name)
                invalid[`phases.${index}.name`] = t('common.validation.this_field_is_required');
            const start = p.startMilestone && milestoneById.get(String(p.startMilestone))?.at
                ? moment(milestoneById.get(String(p.startMilestone)).at)
                : (p.startAt ? moment(p.startAt) : null);
            const stop = p.stopMilestone && milestoneById.get(String(p.stopMilestone))?.at
                ? moment(milestoneById.get(String(p.stopMilestone)).at)
                : (p.stopAt ? moment(p.stopAt) : null);
            if (!start || !stop || !start.isValid() || !stop.isValid())
                invalid[`phases.${index}.bounds`] = t('goals.phase_needs_both_bounds');
            else if (stop.isBefore(start))
                invalid[`phases.${index}.bounds`] = t('goals.phase_stop_before_start');
            return { index, name: p.name, start, stop };
        });

        const sortedForOverlap = [...resolvedPhases]
            .filter((p) => p.start && p.stop && p.start.isValid() && p.stop.isValid())
            .sort((a, b) => a.start!.valueOf() - b.start!.valueOf());
        for (let i = 0; i < sortedForOverlap.length - 1; i++) {
            const current = sortedForOverlap[i];
            const next = sortedForOverlap[i + 1];
            // Sharing the boundary is allowed: the next phase may start on the milestone that ends this one.
            if (current.stop!.valueOf() > next.start!.valueOf()) {
                invalid[`phases.${next.index}.bounds`] = t('goals.phase_overlap', {
                    a: current.name || '…',
                    b: next.name || '…'
                });
            }
        }

        return invalid;
    }

    const addChange = async (change: Change, callback: () => void) => {
        const goals = [
            ...(change.goals || []).map((g: Goal | string) => typeof g === 'string' ? g : g._id),
            goal._id
        ].filter((id, idx, arr) => arr.indexOf(id) === idx);

        const params: AnyObj = {
            _id:  change._id,
            goals
        };
        if (assignPhaseId)
            params.phase = assignPhaseId;

        await dispatch(updateChange(params, /*patch*/true, /*optimistic*/false, callback));
    };

    const removeChange = async (change: Change, callback: () => void) => {
        if (!change)
            return;

        const params: AnyObj = {
            "_id":  change._id,
            "goals": change.goals.filter((g: Goal) => g._id !== goal._id).map((g: Goal) => g._id)
        };
        if (change.phase && (goal.phases || []).some((p) => p._id === change.phase))
            params.phase = null;

        await dispatch(updateChange(params, /*patch*/true, /*optimistic*/false, callback));
    }

    const setChangePhase = async (change: Change, phaseId: string) => {
        await dispatch(updateChange({
            _id: change._id,
            phase: phaseId || null,
            goals: [
                ...(change.goals || []).map((g: Goal | string) => typeof g === 'string' ? g : g._id),
                goal._id
            ].filter((id, idx, arr) => arr.indexOf(id) === idx)
        }, /*patch*/true, /*optimistic*/false));
    };

    const filterAvailableProjects= (projects: Project[], values: AnyObj) => {
        return (projects || []).filter((project: Project) => {
            if ((values.projects || []).find((p: string) => p === project._id))
                return true;

            return false;
        });
    };

    const filterAvailableChanges= (changes: Change[], values: AnyObj) => {
        return (changes || []).filter((change: Change) => {
            if ((values.changes || []).find((c: Change) => c._id === change._id))
                return false;

            return true;
        }).sort((a: Change, b: Change) => b.slug - a.slug);
    };

    const projectOptions = React.useMemo(() => {
        return projects?.map((project: Project) => ({
            value: project._id,
            label: project.name
        }));
    }, [projects]);

    const renderChanges = (values: any, remove: any) => {
        if (!values.changes?.length)
            return null;

        const sortedGroups = Sprint.groupChangesByProject(Sprint.sortChangesBy(values.changes, 'project'));
        const phaseOptions = (values.phases || []).map((p: AnyObj) => ({
            value: p._id,
            label: p.name
        }));

        return (
            <div className={`col-span-${changesPickerVisible ? 2 : 3} flex flex-col space-y-2`}>
                {Object.keys(sortedGroups).map((projectId: string) => (
                    <div key={projectId}>
                        <h3 className="font-bold text-lg">{sortedGroups[projectId].name}</h3>
                        <div className={`grid grid-cols-2 grid-flow-row auto-rows-max gap-x-2 gap-y-2 mb-4`}>
                            {sortedGroups[projectId].changes.map((change: Change) => (
                            <div key={change._id} onClick={() => selectChange(change)} className={`col-span-2 lg:col-span-${changesPickerVisible ? 2 : 1}`} >
                                <ChangeListItem
                                    showProject
                                    onRemove={(change: Change, e: AnyObj) => {
                                        e.stopPropagation();
                                        removeChange(change, () => remove(change));
                                    }}
                                    change={change}
                                />
                                {phaseOptions.length > 0 && (
                                    <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                                        <select
                                            className="block w-full rounded-md border-gray-300 dark:border-gray-600 text-sm"
                                            value={change.phase || ''}
                                            onChange={(e) => setChangePhase(change, e.target.value)}
                                        >
                                            <option value="">{t('goals.no_phase')}</option>
                                            {phaseOptions.map((opt: AnyObj) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                            {change.phaseManual
                                                ? t('goals.phase_manual_hint')
                                                : t('goals.phase_auto_hint')}
                                        </p>
                                    </div>
                                )}
                            </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    const milestoneOptions = (milestones: AnyObj[]) =>
        Goal.sortMilestones((milestones || []).filter((m: AnyObj) => m._id)).map((m: AnyObj) => ({
            value: m._id,
            label: m.name || m._id
        }));

    return (
        <div className="GoalForm bg-white dark:bg-gray-800 border-b px-4 py-6 sm:px-6 lg:px-8">
            <main>
                <Formik
                    initialValues={formInitialValues}
                    enableReinitialize={false}
                    validate={validate}
                    onSubmit={(values) => {
                        onSubmit(values);
                    }}
                >
                    {({ values, setFieldValue, errors, submitCount }) => (
                        <Form>
                            <KeepSortedByDate values={values} setFieldValue={setFieldValue} />
                            <SyncChanges changes={initialValues?.changes || []} setFieldValue={setFieldValue} />
                            <div className="space-y-6">
                                <div className="flex flex-col space-y-4 justify-center">
                                    <div className="grid grid-cols-1 gap-y-2 md:grid-cols-12">
                                        <div className="col-span-12 lg:col-span-6">
                                            <label htmlFor="name" className="block text-center text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                {t('goals.name')}
                                            </label>
                                            <div className="mt-2">
                                                <Field
                                                    type="text"
                                                    name="name"
                                                    className={`h-[42px] text-center block w-full rounded-md ${!isCreation ? 'md:rounded-r-none' : ''} border-1 border-gray-300 dark:border-gray-600 py-1.5 text-gray-900 dark:text-gray-100 font-bold placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                                                    required
                                                />
                                                <ErrorMessage name="name" component="div" className="mt-1 py-1 px-2 text-sm text-white bg-red-600 rounded"/>
                                            </div>
                                        </div>
                                        <div className="col-span-12 lg:col-span-6">
                                            <LabelWithHelp htmlFor="projects" help={t('goals.help.projects') || ''} className="justify-center w-full">
                                                {t('goals.projects')}
                                            </LabelWithHelp>
                                            <div className="mt-2 ml-2 border rounded-md">
                                                <MultiSelectField
                                                    name="projects"
                                                    placeholder={t('changes.choose_a_project') || ''}
                                                    options={projectOptions}
                                                />
                                                <ErrorMessage name="projects" component="div" className="mt-1 py-1 px-2 text-sm text-white bg-red-600 rounded"/>
                                            </div>
                                        </div>
                                        <div className="col-span-12 lg:col-span-6">
                                            <label htmlFor="startAt" className="block text-center text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                {t('goals.startAt')}
                                            </label>
                                            <div className="flex items-center justify-center mt-2">
                                                <DatepickerField name="startAt" unclearable />
                                            </div>
                                        </div>
                                        <div className="col-span-12 lg:col-span-6">
                                            <label htmlFor="stopAt" className="block text-center text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                {t('goals.stopAt')}
                                            </label>
                                            <div className="flex items-center justify-center mt-2">
                                                <DatepickerField name="stopAt" unclearable />
                                            </div>
                                        </div>
                                        <div className="col-span-12 flex justify-center mt-4">
                                            <LabelWithHelp help={t('goals.help.auto_attach') || ''} className="justify-center">
                                                <ToggleField
                                                    name="autoAttachProjectChanges"
                                                    label={t('goals.auto_attach') || ''}
                                                />
                                            </LabelWithHelp>
                                        </div>
                                    </div>
                                </div>

                                <hr />

                                <FieldArray name="milestones">
                                    {({ push, remove }) => (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-lg font-semibold">{t('goals.milestones')}</h3>
                                                <Button
                                                    onClick={() => push({ _id: uuidv4(), name: '', at: null })}
                                                    title={t('goals.add_milestone')}
                                                    color="navigate"
                                                    icon="plus"
                                                    small
                                                />
                                            </div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">{t('goals.help.milestones')}</p>
                                            {(values.milestones || []).map((milestone: AnyObj, index: number) => (
                                                <div key={milestone._id || index} className="grid grid-cols-12 gap-2 items-end border rounded p-3">
                                                    <div className="col-span-12 md:col-span-5">
                                                        <label className="block text-sm font-medium">{t('goals.milestone_name')}</label>
                                                        <Field name={`milestones.${index}.name`} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600" />
                                                        <ErrorMessage name={`milestones.${index}.name`} component="div" className="mt-1 text-sm text-red-600" />
                                                    </div>
                                                    <div className="col-span-10 md:col-span-5">
                                                        <label className="block text-sm font-medium">{t('goals.milestone_at')}</label>
                                                        <div className="mt-1">
                                                            <DatepickerField name={`milestones.${index}.at`} unclearable />
                                                        </div>
                                                    </div>
                                                    <div className="col-span-2 flex justify-end">
                                                        <button type="button" onClick={() => remove(index)} className="text-red-600 p-2" title={t('common.remove') || ''}>
                                                            <Icon type="delete" size={5} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </FieldArray>

                                <hr />

                                <FieldArray name="phases">
                                    {({ push, remove }) => (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-lg font-semibold">{t('goals.phases')}</h3>
                                                <Button
                                                    onClick={() => push({
                                                        _id: uuidv4(),
                                                        name: '',
                                                        startAt: null,
                                                        stopAt: null,
                                                        startMilestone: '',
                                                        stopMilestone: ''
                                                    })}
                                                    title={t('goals.add_phase')}
                                                    color="navigate"
                                                    icon="plus"
                                                    small
                                                />
                                            </div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">{t('goals.help.phases')}</p>
                                            {(values.phases || []).map((phase: AnyObj, index: number) => (
                                                <div key={phase._id || index} className="border rounded p-3 space-y-2">
                                                    <div className="grid grid-cols-12 gap-2 items-end">
                                                        <div className="col-span-10 md:col-span-10">
                                                            <label className="block text-sm font-medium">{t('goals.phase_name')}</label>
                                                            <Field name={`phases.${index}.name`} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600" />
                                                            <ErrorMessage name={`phases.${index}.name`} component="div" className="mt-1 text-sm text-red-600" />
                                                        </div>
                                                        <div className="col-span-2 flex justify-end">
                                                            <button type="button" onClick={() => remove(index)} className="text-red-600 p-2" title={t('common.remove') || ''}>
                                                                <Icon type="delete" size={5} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="block text-sm font-medium">{t('goals.phase_start_milestone')}</label>
                                                            <Field as="select" name={`phases.${index}.startMilestone`} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600">
                                                                <option value="">{t('goals.free_date')}</option>
                                                                {milestoneOptions(values.milestones).map((opt: AnyObj) => (
                                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                ))}
                                                            </Field>
                                                            {!values.phases[index]?.startMilestone && (
                                                                <div className="mt-2">
                                                                    <DatepickerField name={`phases.${index}.startAt`} />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium">{t('goals.phase_stop_milestone')}</label>
                                                            <Field as="select" name={`phases.${index}.stopMilestone`} className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600">
                                                                <option value="">{t('goals.free_date')}</option>
                                                                {milestoneOptions(values.milestones).map((opt: AnyObj) => (
                                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                                ))}
                                                            </Field>
                                                            {!values.phases[index]?.stopMilestone && (
                                                                <div className="mt-2">
                                                                    <DatepickerField name={`phases.${index}.stopAt`} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {submitCount > 0 && errors?.[`phases.${index}.bounds`] && (
                                                        <div className="text-sm text-red-600">{errors[`phases.${index}.bounds`]}</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </FieldArray>

                                <hr />
                                <FieldArray name="changes">
                                    {({ remove, push, pop }) => (
                                        <div className="flex flex-col space-y-4 justify-center">
                                            {(values.phases || []).length > 0 && changesPickerVisible && (
                                                <div className="flex justify-center">
                                                    <label className="text-sm mr-2 self-center">{t('goals.assign_to_phase')}</label>
                                                    <select
                                                        className="rounded-md border-gray-300 dark:border-gray-600 text-sm"
                                                        value={assignPhaseId}
                                                        onChange={(e) => setAssignPhaseId(e.target.value)}
                                                    >
                                                        <option value="">{t('goals.no_phase')}</option>
                                                        {(values.phases || []).map((p: AnyObj) => (
                                                            <option key={p._id} value={p._id}>{p.name || p._id}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                            <div className="grid grid-cols-1 gap-x-4 gap-y-2 lg:grid-cols-3">
                                                {changesPickerVisible && (
                                                    <div className="col-span-1">
                                                        <label htmlFor="name" className="block text-center text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                                                            {t('goals.changes')}
                                                        </label>
                                                        <div className="mt-2">
                                                            <ChangePicker
                                                                onSelect={(change: Change) => addChange(change, () => { push({}); pop(); })}
                                                                className="block w-full"
                                                                listClassName="max-h-[400px] overflow-auto"
                                                                selectClassName="max-h-[400px] overflow-auto"
                                                                filterProjects={(projects: Project[]) => filterAvailableProjects(projects, values)}
                                                                filterChanges={(changes: Change[]) => filterAvailableChanges(changes, values)}
                                                                acceptUnestimated
                                                                acceptFinished={true}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                <div className={`col-span-${changesPickerVisible ? 2 : 3} flex flex-col space-y-2`}>
                                                    {renderChanges(values, remove)}
                                                </div>
                                            </div>
                                            {!changesPickerVisible && (
                                                <div className="col-span-1 mt-2 md:col-span-3 lg:col-span-4 flex justify-center">
                                                    <Button
                                                        onClick={() => { setChangesPickerVisible(true); push({}); pop(); }}
                                                        title={t('sprints.add_cards')}
                                                        color="navigate"
                                                        icon="plus"
                                                        small
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </FieldArray>
                            </div>

                            <SubmitErrorSummary submitCount={submitCount} errors={errors} />

                            <FormActions
                                cancelTo={isCreation ? '/goals' : `/goals/${goal?._id}`}
                                loading={updating === 'pending'}
                            />

                            {change && change._id === currentChangeId && change.project?.name && (
                                <ChangeModal
                                    project={change.project}
                                    change={change}
                                    show={true}
                                    showProjectName={true}
                                    onClose={() => selectChange()}
                                    onRemove={() => { }}
                                />
                            )}
                        </Form>
                    )}
                </Formik>
                {!isCreation && goal?._id && (
                    <DangerZone deleteTo={`/goals/delete/${goal._id}`} />
                )}
            </main>
        </div>
    );
}

export default GoalForm;
